import React, { useEffect, useState } from 'react';
import { Spinner, Alert, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaEnvelopeOpenText } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import { useAppDispatch } from '../../store';
import { acceptInvitation } from '../../features/subscriptions/subscriptionsSlice';

const AcceptInvitationPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError(t('invalid_invitation', 'Invalid or missing invitation token.'));
      setLoading(false);
      return;
    }

    dispatch(acceptInvitation(token))
      .unwrap()
      .then((result) => {
        if (result.checkout_url) {
          window.location.href = result.checkout_url;
        } else {
          setError(t('invitation_no_checkout', 'Could not create checkout session.'));
          setLoading(false);
        }
      })
      .catch((err) => {
        setError(typeof err === 'string' ? err : t('invitation_error', 'Failed to process invitation.'));
        setLoading(false);
      });
  }, [dispatch, searchParams, t]);

  if (loading) {
    return (
      <div className="animate-fade-in d-flex justify-content-center align-items-center" style={{ padding: '3rem 1.5rem', minHeight: 500 }}>
        <div style={{ textAlign: 'center' }}>
          <Spinner animation="border" style={{ color: 'var(--brand-primary)', width: 48, height: 48 }} />
          <p className="mt-3" style={{ color: 'var(--text-muted)' }}>
            {t('processing_invitation', 'Processing your invitation...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in d-flex justify-content-center align-items-center" style={{ padding: '3rem 1.5rem', minHeight: 500 }}>
      <div
        style={{
          maxWidth: 520,
          width: '100%',
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          borderRadius: 20,
          boxShadow: 'var(--shadow-lg)',
          textAlign: 'center',
          padding: '3rem 2rem',
        }}
      >
        <div
          style={{
            width: 80, height: 80, borderRadius: '50%',
            background: isDark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.1)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <FaEnvelopeOpenText size={36} style={{ color: '#ef4444' }} />
        </div>
        <h3 className="fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
          {t('invitation_failed', 'Invitation Error')}
        </h3>
        {error && <Alert variant="danger">{error}</Alert>}
        <Button variant="primary" className="btn-modern btn-primary mt-3" onClick={() => navigate('/subscription')}>
          {t('go_to_subscription', 'Go to Subscription')}
        </Button>
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
